import React, { useEffect, useState } from 'react';
import { IUnsplashImage } from '../../interfaces/unsplash.interface';
import { ImageModalTabKey } from '../../interfaces/common.interface';
import {
    useLazyDownloadUnsplashQuery,
    useLazyQueryUnsplashQuery,
} from '../../store/api/unsplash.api';
import { Button, FormControl, Image, Spinner } from 'react-bootstrap';
import Masonry from 'react-masonry-css';
import ImageLoader from '../loader/ImageLoader';
import InfiniteScroll from 'react-infinite-scroll-component';
import { motion } from 'framer-motion';
import config from '../../config';

interface IProps {
    show: boolean;
    onHide: () => void;
    activeTab: ImageModalTabKey;
    onUpload: (imageUrl: string) => void;
}

const UnsplashImages = ({ show, onHide, activeTab, onUpload }: IProps) => {
    const [queryUnsplash, { isLoading: loadingImages }] = useLazyQueryUnsplashQuery();
    const [downloadUnsplash] = useLazyDownloadUnsplashQuery();

    const [query, setQuery] = useState<string>('');
    const [page, setPage] = useState<number>(1);
    const [images, setImages] = useState<IUnsplashImage[]>([]);
    const [hasNext, setHasNext] = useState<boolean>(true);
    const [tempImage, setTempImage] = useState<IUnsplashImage | null>(null);
    const [hoveredOverImageId, setHoveredOverImageId] = useState<string | null>(null);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    const searchImage = async (queryText: string) => {
        setQuery(queryText);
        setPage(1);
        const response = await queryUnsplash({ query: queryText, page: 1, per_page: 20 });
        if ('data' in response) {
            const data = response.data;
            if (data) {
                setHasNext(data.hasNext);
                setImages(data.results);
            }
        }
    };

    const fetchMoreData = async () => {
        const response = await queryUnsplash({ query, page: page + 1, per_page: 20 });
        if ('data' in response) {
            const data = response.data;
            if (data) {
                setHasNext(data.hasNext);
                setImages((prev) => [...prev, ...data.results]);
                setPage(page + 1);
            }
        }
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowHeight(window.innerHeight - 300);
        };

        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleNo = () => {
        setTempImage(null);
        onHide();
    };

    const handleSelect = () => {
        if (tempImage) {
            downloadUnsplash(tempImage.id);
            const image = tempImage;
            setTempImage(null);
            onUpload(image.urls.raw);
            onHide();
        }
    };

    useEffect(() => {
        const fetchImages = async () => {
            const response = await queryUnsplash({ query: '', page: 1, per_page: 20 });
            if ('data' in response) {
                const data = response.data;
                if (data) {
                    setHasNext(data.hasNext);
                    setImages(data.results);
                }
            }
        };

        if (activeTab === ImageModalTabKey.UNSPLASH) {
            setQuery('');
            setPage(1);
            setImages([]);
            fetchImages();
        }
    }, [activeTab]);

    if (!show) return null;

    return (
        <>
            <div className="py-2">
                <FormControl
                    placeholder="Search image..."
                    aria-label="Search"
                    onChange={(e) => searchImage(e.target.value)}
                />

                {loadingImages ? (
                    <div className="d-flex flex-column gap-2 justify-content-center align-items-center mt-1">
                        <Masonry
                            breakpointCols={3}
                            className="pixii-grid m-1"
                            columnClassName="pixii-grid_column_apprentice"
                            style={{ columnGap: '8px' }}
                        >
                            {' '}
                            {Array.from({ length: 3 }).map((_, index) => (
                                <div key={index}>
                                    <ImageLoader />
                                </div>
                            ))}
                        </Masonry>
                    </div>
                ) : (
                    <InfiniteScroll
                        dataLength={images.length}
                        next={fetchMoreData}
                        hasMore={hasNext}
                        loader={
                            <div
                                className="w-100 d-flex mb-4 justify-content-center align-item-center"
                                style={{ padding: '24px', height: '180px' }}
                            >
                                <Spinner
                                    as="span"
                                    className="mr-1 pe-none"
                                    role="status"
                                    aria-hidden="true"
                                    animation="border"
                                    style={{
                                        marginTop: '40px',
                                        width: '48px',
                                        height: '48px',
                                        color: '#967AA7',
                                        background: 'transparent',
                                    }}
                                />
                            </div>
                        }
                        height={windowHeight}
                        endMessage={
                            <p style={{ textAlign: 'center' }}>
                                <b>Yay! You have seen it all</b>
                            </p>
                        }
                        className="mt-2"
                    >
                        <Masonry
                            breakpointCols={3}
                            className="pixii-grid m-1"
                            columnClassName="pixii-grid_column_apprentice"
                        >
                            {images.map((image, index) => (
                                <div key={index}>
                                    <motion.div
                                        key={index}
                                        initial={{ scale: 0.8, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{ scale: 0.8, opacity: 0 }}
                                        transition={{ duration: 0.5 }}
                                        className={`m-0 ${tempImage && tempImage.id === image.id ? 'border-primary selected border-2' : 'border-0'}`}
                                        onClick={() => {
                                            setTempImage(image);
                                        }}
                                    >
                                        <div
                                            className="px-2 image-delete"
                                            style={{
                                                width: '100%',
                                                position: 'relative',
                                            }}
                                            onMouseOver={() => setHoveredOverImageId(image.id)}
                                            onMouseOut={() => setHoveredOverImageId(null)}
                                        >
                                            <div
                                                className="image-container"
                                                style={{
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    display: 'inline-block',
                                                }}
                                            >
                                                <Image
                                                    src={image.urls.small}
                                                    fluid
                                                    rounded
                                                    className="border border-grey"
                                                    style={{ cursor: 'pointer' }}
                                                />
                                                {hoveredOverImageId &&
                                                    hoveredOverImageId === image.id && (
                                                        <div
                                                            className="image-attribution"
                                                            style={{
                                                                position: 'absolute',
                                                                bottom: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                padding: '8px',
                                                                background:
                                                                    'linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0))',
                                                                color: '#fff',
                                                                fontSize: '12px',
                                                                textAlign: 'center',
                                                                transition: 'opacity 0.3s ease',
                                                            }}
                                                        >
                                                            Photo by{' '}
                                                            <a
                                                                href={
                                                                    image.user.links.html +
                                                                    `?utm_source=${config.unsplashAppName}&utm_medium=referral`
                                                                }
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{
                                                                    color: '#fff',
                                                                    textDecoration: 'underline',
                                                                }}
                                                            >
                                                                {image.user.name}
                                                            </a>{' '}
                                                            on{' '}
                                                            <a
                                                                href={`https://unsplash.com/?utm_source=${config.unsplashAppName}&utm_medium=referral`}
                                                                target="_blank"
                                                                style={{
                                                                    color: '#fff',
                                                                    textDecoration: 'underline',
                                                                }}
                                                            >
                                                                Unsplash
                                                            </a>
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </motion.div>
                                </div>
                            ))}
                        </Masonry>
                    </InfiniteScroll>
                )}
                {!loadingImages && tempImage && (
                    <div className="d-flex justify-content-end gap-2 mt-2">
                        <Button variant="outline-primary" onClick={handleNo}>
                            No
                        </Button>
                        <Button variant="primary" onClick={handleSelect}>
                            Select
                        </Button>
                    </div>
                )}
            </div>
        </>
    );
};

export default UnsplashImages;
